/**
 * "Sky Foundry" — original platformer level #3 (difficulty 5/10).
 * A one-way climb, a spike-lined glider duct, and a two-key door (two keys,
 * one lock) before a hopper run to the gate. Rightward-only completable.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0): void => push({ type: 'spike', ...cell(bx, by) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const slope = (bx: number, by: number, dir: 'up' | 'down'): void =>
  push({ type: 'slope', ...cell(bx, by), props: { dir, grade: 1 } });
const oneway = (bx: number, by: number): void => push({ type: 'oneway', ...cell(bx, by) });
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const checkpoint = (bx: number): void => push({ type: 'checkpoint', ...cell(bx, 1) });
const key = (bx: number, by: number, itemId: number): void =>
  push({ type: 'collectible', ...cell(bx, by), props: { kind: 'key', itemId } });
const lockDoor = (bx: number, itemId: number, needed = 1): void => {
  for (let by = 0; by <= 2; by++)
    push({ type: 'lock', ...cell(bx, by), props: { itemId, needed } });
};
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};

// --- Entry ramps (0–26) ---------------------------------------------------------
spike(12);
spike(13);
slope(18, 0, 'up');
block(19, 0);
block(20, 0);
slope(21, 0, 'down');
checkpoint(24);

// --- One-way climb to the first key (26–52) --------------------------------------
key(30, 1, 1);
oneway(34, 1);
oneway(37, 2);
oneway(40, 3);
ceiling(42, 46, 3); // walkway at the top
push({ type: 'collectible', ...cell(44, 4), props: { kind: 'shard' } });
lockDoor(50, 1);

// --- Glider duct (52–88) ----------------------------------------------------------
portal(56, 'form:glider');
ceiling(58, 84, 6);
for (let bx = 62; bx <= 80; bx += 3) spike(bx);
push({ type: 'collectible', ...cell(72, 4), props: { kind: 'shard' } });
portal(86, 'form:runner', 1);
portal(86, 'form:runner', 4);
checkpoint(88);

// --- Two keys, one door (88–116) ---------------------------------------------------
key(94, 1, 2);
oneway(100, 1);
oneway(102, 2);
key(102, 3, 2); // second key sits on the one-way perch
spike(106);
lockDoor(110, 2, 2); // needs both keys
spike(113);
spike(114);
checkpoint(116);

// --- Hopper run to the gate (116–152) -----------------------------------------------
portal(118, 'form:hopper');
spike(124);
spike(125);
spike(132);
spike(133);
portal(138, 'form:runner', 1);
slope(142, 0, 'up');
block(143, 0);
spike(143, 1);
block(144, 0);
slope(145, 0, 'down');

push({ type: 'collectible', ...cell(128, 2), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(152, 2) });

export const SKY_FOUNDRY: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Sky Foundry',
    author: 'Shape Shifters Team',
    description: 'Two keys up high. One door in the way.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 5,
    tags: ['platformer', 'official'],
  },
  song: { id: 'undercurrent', bpm: 112, offsetMs: 0 },
  settings: {
    mode: 'platformer',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#22d3ee',
    hazardColor: '#fb7185',
    backgroundColor: '#03121f',
  },
  objects,
};
